import React, { useState, useEffect, useRef } from 'react';
import {
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Switch,
    Typography,
    Divider,
    Box,
    IconButton,
    Avatar, Tooltip, Badge,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StorageIcon from '@mui/icons-material/Storage';
import SettingsIcon from '@mui/icons-material/Settings';
import PublicIcon from '@mui/icons-material/Public';
import ForestIcon from '@mui/icons-material/Forest';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import CalculateIcon from '@mui/icons-material/Calculate';

import { useTimezone } from "../contexts/TimezoneContext";
import {useAuth} from "../contexts/AuthContext.tsx";
import {isViewOnly} from "../utils/permissions";
import LogoutIcon from "@mui/icons-material/Logout";
import TimezoneSelector from "./TimezoneSelector.tsx";
import {useNavigate} from "react-router-dom";
import {useUpdateUserPreferences} from "../api/userPreferences.ts";
import { useReleaseNotes, getLastSeenReleaseNoteId, markReleaseNotesAsSeen } from "../api/releaseNotes.ts";
import ReleaseNotesModal from "./ReleaseNotesModal.tsx";
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import MyAIConnectionsDialog from "./MyAIConnectionsDialog.tsx";
import SwitchOrganisationDialog from "./SwitchOrganisationDialog.tsx";
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

export default function UserMenu() {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);
    const [aiConnectionsOpen, setAiConnectionsOpen] = useState(false);
    const [switchOrgOpen, setSwitchOrgOpen] = useState(false);
    const { user, logout, updateUser } = useAuth();
    const viewOnly = isViewOnly(user);
    const updatePreferences = useUpdateUserPreferences();
    const unitSystem = user?.unit_system === 'imperial' ? 'imperial' : 'metric';
    const isMetric = unitSystem === 'metric';
    const { selectedTimezone, projectTimezone } = useTimezone();
    const { data: releaseNotes = [] } = useReleaseNotes();

    const hasUnreadReleaseNotes = releaseNotes.length > 0 &&
        releaseNotes[0].id > getLastSeenReleaseNoteId();

    const hasAutoOpened = useRef(false);

    useEffect(() => {
        if (!hasAutoOpened.current && hasUnreadReleaseNotes) {
            hasAutoOpened.current = true;
            markReleaseNotesAsSeen(releaseNotes[0].id);
            setReleaseNotesOpen(true);
        }
    }, [hasUnreadReleaseNotes]);

    const open = Boolean(anchorEl);

    // Get first initial and capitalize it
    const initial = user?.username?.charAt(0).toUpperCase();

    const handleUserManagement = () => {
        handleClose();
        navigate('/admin');
    };

    const handleDataManagement = () => {
        handleClose();
        navigate('/data-management');
    };

    const handleVegetationManagement = () => {
        handleClose();
        navigate('/vegetation-management');
    };

    const handleAIConnections = () => {
        handleClose();
        setAiConnectionsOpen(true);
    };

    const handleMyAccount = () => {
        handleClose();
        navigate('/account');
    };

    const handleSwitchOrganisation = () => {
        handleClose();
        setSwitchOrgOpen(true);
    };

    // Shown to anyone with somewhere else to go, or currently away from home.
    const canSwitch = (user?.accessible_tenants?.length ?? 0) > 1 || !!user?.is_switched;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleOpenReleaseNotes = () => {
        handleClose();
        if (releaseNotes.length > 0) {
            markReleaseNotesAsSeen(releaseNotes[0].id);
        }
        setReleaseNotesOpen(true);
    };

    const handleToggleUnits = async () => {
        const nextUnitSystem = isMetric ? 'imperial' : 'metric';
        try {
            const result = await updatePreferences.mutateAsync({ unit_system: nextUnitSystem });
            updateUser({ unit_system: result.data.unit_system });
        } catch (error) {
            console.error('Failed to update unit system:', error);
        }
    };

    const handleToggleFrameRate = async () => {
        const currentFrameRate = user?.frame_rate_preference || 'low';
        const nextFrameRate = currentFrameRate === 'low' ? 'high' : 'low';
        try {
            const result = await updatePreferences.mutateAsync({ frame_rate_preference: nextFrameRate });
            updateUser({ frame_rate_preference: result.data.frame_rate_preference });
        } catch (error) {
            console.error('Failed to update frame rate preference:', error);
        }
    };

    const autoRecalculate = user?.auto_recalculate !== false;
    const handleToggleAutoRecalculate = async () => {
        try {
            const result = await updatePreferences.mutateAsync({ auto_recalculate: !autoRecalculate });
            updateUser({ auto_recalculate: result.data.auto_recalculate });
        } catch (error) {
            console.error('Failed to update auto-update preference:', error);
        }
    };

    const isTimezoneMismatch = projectTimezone && selectedTimezone !== projectTimezone;

    return (
        <Box>
            <Tooltip
                title={isTimezoneMismatch ? `Warning: Not using project timezone (${projectTimezone})` : ""}
                arrow
                disableHoverListener={!isTimezoneMismatch}
            >
                <IconButton
                    onClick={handleClick}
                    sx={{
                        p: 0.5,
                        "&:focus": { outline: "none" }
                    }}
                >
                    <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        variant="dot"
                        invisible={!isTimezoneMismatch}
                        sx={{
                            '& .MuiBadge-badge': {
                                backgroundColor: '#ffa726',
                                color: '#ffa726',
                                boxShadow: `0 0 0 2px #1a2027`,
                                '&::after': {
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    animation: 'ripple 1.2s infinite ease-in-out',
                                    border: '1px solid currentColor',
                                    content: '""',
                                },
                            },
                            '@keyframes ripple': {
                                '0%': { transform: 'scale(.8)', opacity: 1 },
                                '100%': { transform: 'scale(2.4)', opacity: 0 },
                            },
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 36,
                                height: 36,
                                bgcolor: '#64b5f6',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                border: open ? '2px solid white' : '2px solid transparent',
                                transition: '0.2s'
                            }}
                        >
                            {initial}
                        </Avatar>
                    </Badge>
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                sx={{ zIndex: 11000 }}
                slotProps={{
                    paper: {
                        sx: {
                            width: 280,
                            bgcolor: '#1a2027',
                            color: 'white',
                            borderRadius: 2,
                            boxShadow: '0px 5px 15px rgba(0,0,0,0.5)',
                            mt: 1.5,
                            overflow: 'visible',
                            zIndex: 11000,
                            // Small arrow pointing to the avatar
                            '&:before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                right: 14,
                                width: 10,
                                height: 10,
                                bgcolor: '#1a2027',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                            },
                        }
                    }
                }}
            >
                {/* Header Section: User Info */}
                <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                        Logged in as
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {user?.home_user?.username ?? user?.username}
                    </Typography>
                    {user?.tenant_name && (
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                            {user?.is_switched ? `Working in ${user.tenant_name}` : user.tenant_name}
                        </Typography>
                    )}
                </Box>

                {/* First Divider: No top margin, but a slight bottom negative margin to "tuck" under the header */}
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', m: 0 }} />

                {/* Section 1: Navigation */}
                {user?.is_staff && (
                    <>
                        <MenuItem
                            onClick={handleUserManagement}
                            sx={{
                                py: 1.5, // 12px vertical padding
                                '&.MuiMenuItem-root': { minHeight: 'auto' }
                            }}
                        >
                            <ListItemIcon>
                                <DashboardIcon sx={{ color: 'rgba(255,255,255,0.7)' }} fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Admin"
                                slotProps={{
                                    primary: {
                                        style: { lineHeight: '1.5rem' }
                                    }
                                }}
                            />
                        </MenuItem>

                        {/* Second Divider: Explicitly zeroing everything to match the first */}
                        <Divider sx={{
                            borderColor: 'rgba(255,255,255,0.1)',
                            margin: '0 !important', // Force override of any MUI List styles
                        }} />
                    </>
                )}

                {!viewOnly && (
                <MenuItem
                    onClick={handleDataManagement}
                    sx={{
                        py: 1.5,
                        '&.MuiMenuItem-root': { minHeight: 'auto' }
                    }}
                >
                    <ListItemIcon>
                        <StorageIcon sx={{ color: 'rgba(255,255,255,0.7)' }} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary="Data Management"
                        slotProps={{
                            primary: {
                                style: { lineHeight: '1.5rem' }
                            }
                        }}
                    />
                </MenuItem>
                )}

                {!viewOnly && (
                <MenuItem
                    onClick={handleVegetationManagement}
                    sx={{
                        py: 1.5,
                        '&.MuiMenuItem-root': { minHeight: 'auto' }
                    }}
                >
                    <ListItemIcon>
                        <ForestIcon sx={{ color: 'rgba(255,255,255,0.7)' }} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary="Vegetation Management"
                        slotProps={{
                            primary: {
                                style: { lineHeight: '1.5rem' }
                            }
                        }}
                    />
                </MenuItem>
                )}

                {/* Visible to everyone (incl. view-only): connections are the
                    user's own credentials and revoking only narrows access. */}
                <MenuItem
                    onClick={handleAIConnections}
                    sx={{
                        py: 1.5,
                        '&.MuiMenuItem-root': { minHeight: 'auto' }
                    }}
                >
                    <ListItemIcon>
                        <SmartToyIcon sx={{ color: 'rgba(255,255,255,0.7)' }} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary="AI Assistants"
                        slotProps={{
                            primary: {
                                style: { lineHeight: '1.5rem' }
                            }
                        }}
                    />
                </MenuItem>

                {canSwitch && (
                <MenuItem
                    onClick={handleSwitchOrganisation}
                    sx={{
                        py: 1.5,
                        '&.MuiMenuItem-root': { minHeight: 'auto' }
                    }}
                >
                    <ListItemIcon>
                        <SwapHorizIcon sx={{ color: 'rgba(255,255,255,0.7)' }} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary="Switch organisation"
                        slotProps={{
                            primary: {
                                style: { lineHeight: '1.5rem' }
                            }
                        }}
                    />
                </MenuItem>
                )}

                {/* Email, phone (SMS notifications) and password. */}
                <MenuItem
                    onClick={handleMyAccount}
                    sx={{
                        py: 1.5,
                        '&.MuiMenuItem-root': { minHeight: 'auto' }
                    }}
                >
                    <ListItemIcon>
                        <ManageAccountsIcon sx={{ color: 'rgba(255,255,255,0.7)' }} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary="My Account"
                        slotProps={{
                            primary: {
                                style: { lineHeight: '1.5rem' }
                            }
                        }}
                    />
                </MenuItem>

                <Divider sx={{
                    borderColor: 'rgba(255,255,255,0.1)',
                    margin: '0 !important',
                }} />

                {/* Section 2: Unit Preferences */}
                <MenuItem disableRipple sx={{ cursor: 'default', '&:hover': { bgcolor: 'transparent' } }}>
                    <ListItemIcon>
                        <SettingsIcon sx={{ color: 'rgba(255,255,255,0.7)' }} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary="Units"
                        secondary={isMetric ? "Metric" : "Imperial"}
                        slotProps={{
                            secondary: {
                                sx: { color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }
                            }
                        }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                        <Box
                            component="img"
                            src="/flags/gb.svg"
                            alt="UK"
                            sx={{ width: 18, height: 14, mr: 1, borderRadius: '2px' }}/>
                        <Switch
                            size="small"
                            checked={!isMetric}
                            onChange={handleToggleUnits}
                            disabled={!user || updatePreferences.isPending}
                            sx={{
                                // Customize the thumb (the moving circle)
                                '& .MuiSwitch-thumb': {
                                    backgroundColor: 'white',
                                },
                                // Customize the track (the background bar)
                                '& .MuiSwitch-track': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.2) !important',
                                    opacity: '1 !important',
                                },
                                // Remove the default green color when checked
                                '& .Mui-checked': {
                                    '& + .MuiSwitch-track': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.2) !important',
                                        opacity: '1 !important',
                                    },
                                },
                                // Optional: darken flags when not selected to emphasize choice
                                mx: 0.5
                            }}
                        />
                        <Box
                            component="img"
                            src="/flags/us.svg"
                            alt="US"
                            sx={{ width: 18, height: 14, ml: 1, borderRadius: '2px' }}
                        />
                    </Box>
                </MenuItem>

                {/* Frame Rate Preference */}
                <MenuItem disableRipple sx={{ cursor: 'default', '&:hover': { bgcolor: 'transparent' } }}>
                    <ListItemIcon>
                        <SettingsIcon sx={{ color: 'rgba(255,255,255,0.7)' }} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary="Frame Rate"
                        secondary={user?.frame_rate_preference === 'high' ? "High (240 frames)" : "Low (60 frames)"}
                        slotProps={{
                            secondary: {
                                sx: { color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }
                            }
                        }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', mr: 1 }}>
                            Low
                        </Typography>
                        <Switch
                            size="small"
                            checked={user?.frame_rate_preference === 'high'}
                            onChange={handleToggleFrameRate}
                            disabled={!user || updatePreferences.isPending}
                            sx={{
                                '& .MuiSwitch-thumb': {
                                    backgroundColor: 'white',
                                },
                                '& .MuiSwitch-track': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.2) !important',
                                    opacity: '1 !important',
                                },
                                '& .Mui-checked': {
                                    '& + .MuiSwitch-track': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.2) !important',
                                        opacity: '1 !important',
                                    },
                                },
                                mx: 0.5
                            }}
                        />
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', ml: 1 }}>
                            High
                        </Typography>
                    </Box>
                </MenuItem>

                {/* Auto-update Preference */}
                <MenuItem disableRipple sx={{ cursor: 'default', '&:hover': { bgcolor: 'transparent' } }}>
                    <ListItemIcon>
                        <CalculateIcon sx={{ color: 'rgba(255,255,255,0.7)' }} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary="Auto-update"
                        secondary={autoRecalculate ? "Fuel loads recalculate after edits" : "Use the Recalculate button"}
                        slotProps={{
                            secondary: {
                                sx: { color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }
                            }
                        }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', mr: 1 }}>
                            Off
                        </Typography>
                        <Switch
                            size="small"
                            checked={autoRecalculate}
                            onChange={handleToggleAutoRecalculate}
                            disabled={!user || updatePreferences.isPending}
                            sx={{
                                '& .MuiSwitch-thumb': {
                                    backgroundColor: 'white',
                                },
                                '& .MuiSwitch-track': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.2) !important',
                                    opacity: '1 !important',
                                },
                                '& .Mui-checked': {
                                    '& + .MuiSwitch-track': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.2) !important',
                                        opacity: '1 !important',
                                    },
                                },
                                mx: 0.5
                            }}
                        />
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', ml: 1 }}>
                            On
                        </Typography>
                    </Box>
                </MenuItem>

                {/* Section 3: Timezone Configuration */}
                <MenuItem
                    disableRipple
                    sx={{
                        cursor: 'default',
                        '&:hover': { bgcolor: 'transparent' },
                        display: 'flex',
                        alignItems: 'center',
                        py: 1
                    }}
                >
                    <Tooltip
                        title={isTimezoneMismatch ? `Project uses ${projectTimezone}` : ""}
                        arrow
                        placement="left"
                        disableHoverListener={!isTimezoneMismatch}
                    >
                        <ListItemIcon>
                            <PublicIcon
                                sx={{
                                    // Highlight orange if mismatch, otherwise standard grey
                                    color: isTimezoneMismatch ? '#ffa726' : 'rgba(255,255,255,0.7)',
                                    transition: 'color 0.3s ease',
                                    // Optional: add a slight glow/drop shadow when mismatched
                                    filter: isTimezoneMismatch ? 'drop-shadow(0px 0px 4px rgba(255, 167, 38, 0.4))' : 'none'
                                }}
                                fontSize="small"
                            />
                        </ListItemIcon>
                    </Tooltip>

                    <ListItemText
                        primary="Timezone"
                        secondary={selectedTimezone}
                        sx={{ mr: 2, flex: '0 1 auto' }}
                        slotProps={{
                            primary: {
                                sx: {
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    // Subtle text color shift to draw attention
                                    color: isTimezoneMismatch ? '#ffa726' : 'inherit'
                                }
                            },
                            secondary: {
                                sx: { color: isTimezoneMismatch ? '#ffa726' : 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }
                            }
                        }}
                    />

                    {/* The Dropdown now sits to the right of the text */}
                    <Box sx={{
                        flexGrow: 1,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        ml: 2 // Minimum gap between text and dropdown
                    }}>
                        <TimezoneSelector />
                    </Box>
                </MenuItem>

                {/* RELEASE NOTES SECTION */}
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', margin: '0 !important' }} />
                <MenuItem
                    onClick={handleOpenReleaseNotes}
                    sx={{ py: 1.5, '&.MuiMenuItem-root': { minHeight: 'auto' } }}
                >
                    <ListItemIcon>
                        <NewReleasesIcon
                            sx={{ color: 'rgba(255,255,255,0.7)' }}
                            fontSize="small"
                        />
                    </ListItemIcon>
                    <ListItemText
                        primary="What's New"
                        slotProps={{ primary: { style: { lineHeight: '1.5rem' } } }}
                    />
                </MenuItem>

                {/* LOGOUT SECTION */}
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mt: 1 }} />
                <MenuItem
                    onClick={() => { logout?.(); handleClose(); }}
                    sx={{
                        py: 1.5,
                        color: '#ff5252', // Red color for logout
                        '&:hover': {
                            bgcolor: 'rgba(255, 82, 82, 0.08)', // Soft red hover
                        }
                    }}
                >
                    <ListItemIcon>
                        <LogoutIcon sx={{ color: '#ff5252' }} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary="Sign Out"
                        slotProps={{
                            primary: {
                                fontWeight: 600
                            }
                        }}
                    />
                </MenuItem>
            </Menu>

            <ReleaseNotesModal
                open={releaseNotesOpen}
                onClose={() => setReleaseNotesOpen(false)}
                releaseNotes={releaseNotes}
            />

            <SwitchOrganisationDialog open={switchOrgOpen} onClose={() => setSwitchOrgOpen(false)} />

            <MyAIConnectionsDialog
                open={aiConnectionsOpen}
                onClose={() => setAiConnectionsOpen(false)}
            />
        </Box>
    );
}
